import { useSnapshot } from 'valtio'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Check, Trash2, Copy, Pin, PanelRight } from 'lucide-react'
import { store, actions } from '@/store'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { TodoItemInfoGrid } from './todo-item-info-grid'
import { TodoItemFooter } from './todo-item-footer'
import { useRightSidebarResize } from './hooks/use-right-sidebar-resize'
import { useEffect, useRef, useState, useCallback, memo } from 'react'
import { useDebounce } from '@/hooks/use-debounce'
import { PrioritySelector, StatusSelectorWrapper } from './todo-item-meta-row'
import { toast } from 'sonner'
import { haptics } from '@/lib/haptics'
import { undo } from '@/store'
import { showUndoToast } from '@/lib/toast-utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { BottomSheet } from '@/components/ui/bottom-sheet'

export function TaskDetailPanel({ todo, isMobile, handleToggleRightPanel }) {
  // Scope snapshot to only UI settings to avoid re-renders on every todo change
  const uiSnap = useSnapshot(store.uiSettings)
  const [isResizing, setIsResizing] = useState(false)
  const { handleRightSidebarResizeMouseDown } = useRightSidebarResize(store, setIsResizing)
  const showRightPanel = uiSnap.showRightPanel

  const prevShowSidebar = useRef(uiSnap.showSidebar)
  const prevShowRightPanel = useRef(uiSnap.showRightPanel)

  useEffect(() => {
    if (isMobile) return undefined

    const syncPanelFit = () => {
      const viewportWidth = window.innerWidth || 0
      const leftWidth = uiSnap.showSidebar ? Math.min(uiSnap.sidebarWidth, 0.34 * viewportWidth) : 0
      const rightWidth = uiSnap.showRightPanel
        ? Math.min(uiSnap.rightSidebarWidth, 0.4 * viewportWidth)
        : 0

      const minReadableMain = 320
      const isTight = viewportWidth - leftWidth - rightWidth < minReadableMain
      const bothOpen = uiSnap.showSidebar && uiSnap.showRightPanel

      if (isTight && bothOpen) {
        // If the user just opened the sidebar, auto-close the right panel to make room
        const sidebarJustOpened = !prevShowSidebar.current && uiSnap.showSidebar

        if (sidebarJustOpened) {
          actions.setUIVisibility('showRightPanel', false)
        } else if (todo) {
          // If we have an active task, prioritize it over the sidebar on constrained screens
          actions.setUIVisibility('showSidebar', false)
        } else {
          // No active task, sidebar is more important than the "No task selected" panel
          actions.setUIVisibility('showRightPanel', false)
        }
      }

      prevShowSidebar.current = uiSnap.showSidebar
      prevShowRightPanel.current = uiSnap.showRightPanel
    }

    syncPanelFit()
    window.addEventListener('resize', syncPanelFit)
    return () => window.removeEventListener('resize', syncPanelFit)
  }, [
    isMobile,
    uiSnap.showRightPanel,
    uiSnap.showSidebar,
    uiSnap.sidebarWidth,
    uiSnap.rightSidebarWidth,
  ])

  const handleClose = () => {
    if (todo) {
      actions.updateTodo(todo.id, { isExpanded: false })
    }
  }

  // Mobile: Use Bottom Sheet
  if (isMobile) {
    return (
      <BottomSheet
        isOpen={!!todo}
        onClose={handleClose}
        initialSnapPoint="half"
        className="z-[200]"
      >
        {todo && <TaskDetailContent todo={todo} uiSettings={uiSnap} isMobile={true} />}
      </BottomSheet>
    )
  }

  // Desktop: Use Side Panel
  const isVisible = showRightPanel && !!todo

  return (
    <>
      {todo && (
        <RightPanelToggleFixed
          showRightPanel={showRightPanel}
          handleToggleRightPanel={handleToggleRightPanel}
        />
      )}
      <motion.div
        initial={false}
        animate={{
          width: isVisible ? uiSnap.rightSidebarWidth : 0,
          opacity: isVisible ? 1 : 0,
        }}
        exit={{ width: 0, opacity: 0 }}
        transition={
          isResizing
            ? { type: 'tween', duration: 0 }
            : { type: 'spring', stiffness: 300, damping: 30 }
        }
        id="task-detail-panel"
        className={cn(
          'task-detail-panel bg-muted/30 flex flex-col h-full overflow-hidden relative shrink-0',
          'max-w-[40vw] min-w-0',
          isVisible ? 'border-l border-border/10' : 'border-l-0',
        )}
        style={{ width: isVisible ? uiSnap.rightSidebarWidth : 0 }}
      >
        {/* Resize Handle */}
        {isVisible && (
          <div
            className="absolute top-0 bottom-0 left-0 w-1 cursor-col-resize hover:bg-primary/10 transition-colors z-50 hover:w-1.5"
            onMouseDown={handleRightSidebarResizeMouseDown}
          />
        )}

        {todo && <TaskDetailContent todo={todo} uiSettings={uiSnap} isMobile={false} />}
      </motion.div>
    </>
  )
}

const RightPanelToggleFixed = ({ showRightPanel, handleToggleRightPanel }) => (
  <Button
    variant="outline"
    size="icon"
    onClick={handleToggleRightPanel}
    className="fixed top-3 right-3 z-[100] shadow-sm group hidden md:flex"
  >
    <PanelRight
      className={cn(
        'h-[1.2rem] w-[1.2rem] text-muted-foreground transition-all duration-300',
        'group-hover:text-primary',
        !showRightPanel && 'rotate-180',
      )}
    />
  </Button>
)

const TaskDetailContent = memo(({ todo, uiSettings, isMobile = false }) => {
  // Local state for header inputs
  const [title, setTitle] = useState(todo?.text || '')
  const titleRef = useRef(null)

  // Auto-resize title textarea
  useEffect(() => {
    if (titleRef.current) {
      const el = titleRef.current
      el.style.height = 'auto'
      const newHeight = Math.min(el.scrollHeight, 200)
      el.style.height = `${newHeight}px`
    }
  }, [title])

  // Sync state if todo changes (e.g. switching tasks)
  useEffect(() => {
    setTitle(todo.text)
  }, [todo.id, todo.text])

  const debouncedTitle = useDebounce(title, 300)

  useEffect(() => {
    if (debouncedTitle !== todo.text && debouncedTitle !== undefined) {
      actions.updateTodo(todo.id, { text: debouncedTitle })
    }
  }, [debouncedTitle, todo.id])

  const handleClose = () => {
    actions.updateTodo(todo.id, { isExpanded: false })
  }

  const handleUpdate = (id, updates) => {
    actions.updateTodo(id, updates)
  }

  const handleTitleChange = (e) => {
    setTitle(e.target.value)
  }

  const handleToggleComplete = () => {
    actions.updateTodo(todo.id, {
      completed: !todo.completed,
      status: !todo.completed ? 'done' : 'todo',
    })
  }

  const handleDelete = () => {
    actions.deleteTodo(todo.id)
    showUndoToast('Task deleted', <Trash2 className="h-4 w-4 text-destructive" />)
  }

  const handleDuplicate = () => {
    actions.duplicateTodo(todo.id)
    toast.success('Task duplicated')
  }

  const handleTogglePin = () => {
    const willBePinned = !todo.pinned
    actions.togglePinned(todo.id)
    haptics.impact()
    toast(willBePinned ? 'Task pinned' : 'Task unpinned', {
      icon: <Pin className="h-4 w-4 text-primary" />,
    })
  }

  const existingStatuses = Array.from(
    new Set(store.todos.map((t) => t.status).concat(['todo', 'done'])),
  )

  const [subtaskText, setSubtaskText] = useState('')
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(false)

  return (
    <div className="flex flex-col h-full min-w-0 w-full">
      {/* Header */}
      <div
        className={cn(
          'flex items-center py-2 border-b border-border/10 shrink-0 gap-x-2 gap-y-2',
          isMobile ? 'flex-col items-start px-3' : 'flex-wrap pl-2 pr-2 sm:pl-4 sm:pr-14',
        )}
      >
        <div className={cn('flex gap-2', isMobile ? 'w-full flex-wrap' : 'flex-wrap')}>
          <div className="flex flex-col gap-0.5 min-w-[80px]">
            <span className="text-[10px] font-bold uppercase text-muted-foreground/50 tracking-wider leading-none ml-1">
              Priority
            </span>
            <PrioritySelector
              priority={todo.priority}
              handlePriorityChange={(p) => handleUpdate(todo.id, { priority: p })}
              stopPropagation={(e) => e?.stopPropagation()}
            />
          </div>

          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase text-muted-foreground/50 tracking-wider leading-none ml-1">
              Status
            </span>
            <StatusSelectorWrapper
              status={todo.status}
              existingStatuses={existingStatuses}
              handleStatusChange={(s) =>
                handleUpdate(todo.id, { status: s, completed: s === 'done' })
              }
              stopPropagation={(e) => e?.stopPropagation()}
            />
          </div>
        </div>

        <div
          className={cn(
            'flex items-center gap-1 shrink-0',
            isMobile ? 'w-full justify-end' : 'ml-auto self-end pb-1',
          )}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleTogglePin}
                className={cn('h-8 w-8', todo.pinned ? 'text-primary' : 'text-muted-foreground')}
              >
                <Pin className="h-4 w-4" fill={todo.pinned ? 'currentColor' : 'none'} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{todo.pinned ? 'Unpin' : 'Pin'}</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={handleDuplicate} className="h-8 w-8">
                <Copy className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Duplicate</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDelete}
                className="h-8 w-8 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Delete</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-2 sm:p-4 flex flex-col min-w-0">
        {/* Title Editor */}
        <div className="flex items-start gap-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleToggleComplete}
                className={cn(
                  'rounded-full border h-6 w-6 p-0 mt-1 shrink-0',
                  todo.completed
                    ? 'bg-primary border-primary text-primary-foreground'
                    : 'border-muted-foreground/40',
                )}
              >
                {todo.completed && <Check className="h-3.5 w-3.5" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{todo.completed ? 'Undo' : 'Mark Done'}</p>
            </TooltipContent>
          </Tooltip>

          <textarea
            ref={titleRef}
            className="flex-1 min-w-0 bg-transparent text-lg sm:text-xl font-bold border-none resize-none focus:ring-0 p-0 leading-tight focus:outline-none transition-[height] duration-200"
            value={title}
            onChange={handleTitleChange}
            onKeyDown={(e) => {
              if (e.key === 'Escape') e.currentTarget.blur()
            }}
            placeholder="Task name"
            rows={1}
            style={{ maxHeight: '200px', overflowY: 'auto' }}
          />
        </div>

        <TodoItemInfoGrid
          todo={todo}
          enabledFeatures={uiSettings.enabledFeatures || {}}
          onUpdate={handleUpdate}
          subtaskText={subtaskText}
          onSubtaskTextChange={setSubtaskText}
          viewMode="list"
        />
      </div>

      {/* Footer - Fixed at bottom */}
      <div className="px-2 pb-2 sm:px-4 sm:pb-4 bg-transparent shrink-0">
        <TodoItemFooter
          todo={todo}
          enabledFeatures={uiSettings.enabledFeatures || {}}
          onUpdate={handleUpdate}
          isCommentsExpanded={isCommentsExpanded}
          onToggleComments={() => setIsCommentsExpanded(!isCommentsExpanded)}
          viewMode="list"
        />
      </div>
    </div>
  )
})
